import { Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbButton, BreadcrumbDivider,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useEpicTree, useMyProjects } from '../../api/hooks';
import type { Project, EpicTreeNode } from '../../api/types';

/* Route-aware breadcrumb rendered once in the layout, so it appears on every
   in-app page automatically. Dynamic segments resolve to friendly labels:
   project GUID → project name (from the cached projects list), epic id → the
   epic's customer/system title (from the cached epic-tree query, when present). */

const useStyles = makeStyles({
  // Pinned above the scroll region in TeamsAppLayout — carries its own padding
  // so it aligns with content (24px) and has top breathing room.
  bar: {
    flexShrink: 0,
    paddingTop: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
  },
});

interface Crumb { label: string; to?: string }

// Sub-route segment → label. Anything not listed falls back to Title Case.
const LABELS: Record<string, string> = {
  // programme
  'concurrent-projects': 'Concurrent timeline',
  burnup: 'Burnup & forecast',
  wsjf: 'WSJF backlog',
  'value-at-risk': 'Value at risk',
  'bug-aging': 'Bug aging',
  'cost-vs-estimate': 'Cost vs estimate',
  activity: 'Activity heatmap',
  cfd: 'Cumulative flow',
  'spec-quality': 'Spec quality',
  'cycle-time': 'WIP cycle time',
  // customer
  roadmap: 'Your roadmap',
  progress: 'Progress',
  upcoming: 'Upcoming',
  'decisions-risks': 'Decisions & risks',
  'signoff-queue': 'Sign-off queue',
  // admin
  'source-tenants': 'Source tenants',
  projects: 'Projects',
  users: 'Users',
  config: 'Central config',
  // epic leaves
  tree: 'Work breakdown',
  velocity: 'Velocity',
};

function titleCase(seg: string): string {
  return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function label(seg: string): string {
  return LABELS[seg] ?? titleCase(seg);
}

function buildCrumbs(
  pathname: string,
  projects: Project[] | undefined,
  epicTitle: (projectId: string, epicId: string) => string | null,
  featureTitle: (projectId: string, epicId: string, featureId: string) => string | null,
): Crumb[] {
  const p = pathname.split('/').filter(Boolean);
  const crumbs: Crumb[] = [{ label: 'Home', to: '/' }];
  if (p.length === 0) return crumbs;

  if (p[0] === 'reports') {
    // Feature Reports is its own top-level category, not under the project list.
    if (p[1] === 'features') {
      crumbs.push({ label: 'Feature Reports' });
      return crumbs;
    }
    // Feature Progress is reached from Feature Reports — so its trail is
    // Home / Feature Reports / <feature>, not the project→epic hierarchy.
    // Route: /reports/projects/:projectId/epics/:epicId/features/:featureId/progress
    if (p[1] === 'projects' && p[3] === 'epics' && p[5] === 'features' && p[6]) {
      crumbs.push({ label: 'Feature Reports', to: '/reports/features' });
      crumbs.push({ label: featureTitle(p[2], p[4], p[6]) ?? 'Feature' });
      return crumbs;
    }
    // The /reports landing page is the project list (titled "Projects").
    crumbs.push({ label: 'Projects', to: '/reports' });

    if (p[1] === 'projects' && p[2]) {
      const proj = projects?.find((x) => x.id === p[2]);
      const base = `/reports/projects/${p[2]}`;
      // The project-name crumb IS the project's epics list (its page hero shows the
      // project name) — so it's the leaf on the epics page, no extra "Projects" crumb.
      crumbs.push({ label: proj?.name ?? 'Project', to: `${base}/epics` });

      if (p[3] === 'epics') {
        if (p[4]) {
          // Prefer the cached epic title; fall back to the project name rather
          // than a raw "Epic 553" id, which reads poorly before the tree loads.
          const t = epicTitle(p[2], p[4]) ?? proj?.name ?? 'Project';
          // p[5] = progress | tree | velocity (the actual page)
          crumbs.push(p[5] ? { label: t, to: `${base}/epics/${p[4]}/progress` } : { label: t });
          if (p[5]) crumbs.push({ label: label(p[5]) });
        }
      } else if (p[3] === 'velocity') {
        crumbs.push({ label: 'Velocity' });
      }
    } else if (p[1] === 'programme') {
      crumbs.push({ label: 'Programme', to: '/reports/programme' });
      if (p[2]) crumbs.push({ label: label(p[2]) });
    } else if (p[1] === 'customer') {
      crumbs.push({ label: 'For customer', to: '/reports/customer' });
      if (p[2]) crumbs.push({ label: label(p[2]) });
    }
  } else if (p[0] === 'admin') {
    crumbs.push({ label: 'Admin', to: '/admin' });
    if (p[1]) crumbs.push({ label: label(p[1]) });
  } else {
    crumbs.push({ label: label(p[0]) });
  }

  return crumbs;
}

export function AppBreadcrumbs() {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: projects } = useMyProjects();

  // On an epic route, reactively read the (shared) epic-tree query so the crumb
  // updates to the epic's Customer Title once the tree loads. Parsing the path
  // here — not a hook — lets us call useEpicTree unconditionally; it's disabled
  // (no fetch) off epic routes and dedupes with the page's own query on them.
  const segs = location.pathname.split('/').filter(Boolean);
  const onEpicRoute = segs[0] === 'reports' && segs[1] === 'projects' && segs[3] === 'epics';
  const epicProjectId = onEpicRoute ? segs[2] : undefined;
  const epicIdNum = onEpicRoute && segs[4] ? Number(segs[4]) : undefined;
  const { data: epicTree } = useEpicTree(epicProjectId, epicIdNum);

  const epicTitle = (projectId: string, epicId: string): string | null => {
    if (!epicTree || projectId !== epicProjectId || Number(epicId) !== epicIdNum) return null;
    return epicTree.customerTitle?.trim() || epicTree.title || null;
  };

  // Resolve a feature's friendly title from the cached epic tree (feature-progress route).
  const featureTitle = (projectId: string, epicId: string, featureId: string): string | null => {
    if (!epicTree || projectId !== epicProjectId || Number(epicId) !== epicIdNum) return null;
    const find = (node: EpicTreeNode): EpicTreeNode | null => {
      if (node.id === Number(featureId)) return node;
      for (const c of node.children ?? []) { const f = find(c); if (f) return f; }
      return null;
    };
    const node = find(epicTree);
    return node ? (node.customerTitle?.trim() || node.title || null) : null;
  };

  const crumbs = buildCrumbs(location.pathname, projects, epicTitle, featureTitle);
  if (crumbs.length <= 1) return null; // don't show a lone "Home" crumb

  const lastIndex = crumbs.length - 1;
  return (
    <Breadcrumb className={styles.bar} aria-label="Breadcrumb" size="medium">
      {crumbs.map((c, i) => (
        <Fragment key={`${c.label}-${i}`}>
          <BreadcrumbItem>
            <BreadcrumbButton
              current={i === lastIndex}
              onClick={c.to && i !== lastIndex ? () => navigate(c.to as string) : undefined}
            >
              {c.label}
            </BreadcrumbButton>
          </BreadcrumbItem>
          {i !== lastIndex && <BreadcrumbDivider />}
        </Fragment>
      ))}
    </Breadcrumb>
  );
}
